import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Card, Container } from 'react-bootstrap';
import useSensorManagement from '../../../Hooks/useSensor';
import Loading from '../../../components/LoadingPage/Loading';
import './SensorAlertAcknowledgement.scss';

// ─── Types ─────────────────────────────────────────────────────────────────
interface AckResult {
    acknowledgedBy: string;
    acknowledgedAt: string;
    refrigeratorType: string;
    refrigeratorName: string;
    alertType: string;
    reading: number;
    deviation: number;
}

interface AckResponse {
    status: number;
    message: string;
    success: boolean;
    errorDetails: null | string;
    result: AckResult | null;
}

type ViewState = 'loading' | 'success' | 'already-acknowledged' | 'error' | 'missing-id';

function isAlreadyAcknowledgedMessage(message: string | undefined | null): boolean {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return normalized.includes('already') && normalized.includes('acknowledged');
}

function resolveViewState(id: string | undefined, ackRes: AckResponse | null): ViewState {
    if (!id) return 'missing-id';
    if (!ackRes) return 'loading';
    if (ackRes.success && ackRes.result) return 'success';
    if (isAlreadyAcknowledgedMessage(ackRes.message)) return 'already-acknowledged';
    return 'error';
}

// Alert type is treated as an opaque string coming from the backend.
// We only special-case BATTERY vs everything-else (temperature-style, sign-based).
function isBatteryAlert(alertType: string): boolean {
    return alertType?.toUpperCase().includes('BATTERY');
}

// Plain sign check — no severity buckets, no thresholds, no math.
function getAlertLabel(alertType: string, deviation: number): string {
    if (isBatteryAlert(alertType)) return 'Low Battery Alert';
    return deviation < 0 ? 'Low Temperature Alert' : 'Maximum Temperature Alert';
}

function formatDateTime(iso: string): string {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date} · ${time}`;
}

const SensorAlertAcknowledgement: React.FC = () => {
    const { id } = useParams();
    const [ackRes, setAckRes] = useState<AckResponse | null>(null);
    const { acknowledgeSensorAlertAPI, acknowledgeSensorAlertLoading } = useSensorManagement();

    const handleAcknowledgeSensorAlert = useCallback(async () => {
        if (!id) return;
        try {
            const res = await acknowledgeSensorAlertAPI({ id });
            setAckRes(res || null);
        } catch (err: any) {
            setAckRes({
                status: err?.status ?? 500,
                message: err?.message || 'Something went wrong while acknowledging this alert.',
                success: false,
                errorDetails: err?.message ?? null,
                result: null,
            });
        }
    }, [id, acknowledgeSensorAlertAPI]);

    useEffect(() => {
        if (!id) return;
        handleAcknowledgeSensorAlert();
    }, [id]);

    const viewState = resolveViewState(id, ackRes);

    if (acknowledgeSensorAlertLoading || viewState === 'loading') {
        return <Loading />;
    }

    if (viewState === 'missing-id') {
        return (
            <Container fluid className="ack-page-wrapper">
                <Card className="ack-card" role="alert">
                    <Card.Body className="ack-header ack-header--error">
                        <div className="ack-icon-ring">
                            <i className="bi bi-exclamation-circle" />
                        </div>
                        <Card.Title as="h1" className="ack-title">Invalid Link</Card.Title>
                        <Card.Text className="ack-subtitle">
                            This acknowledgement link is missing required information.
                        </Card.Text>
                    </Card.Body>
                    <Card.Body className="ack-body">
                        <p className="ack-plain-message">
                            Please use the link from the original alert notification. If the problem continues,
                            contact your system administrator.
                        </p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (viewState === 'error') {
        return (
            <Container fluid className="ack-page-wrapper">
                <Card className="ack-card" role="alert" aria-live="assertive">
                    <Card.Body className="ack-header ack-header--error">
                        <div className="ack-icon-ring">
                            <i className="bi bi-exclamation-circle" />
                        </div>
                        <Card.Title as="h1" className="ack-title">Couldn't Acknowledge Alert</Card.Title>
                        <Card.Text className="ack-subtitle">
                            {ackRes?.message || 'Something went wrong. Please try again.'}
                        </Card.Text>
                    </Card.Body>
                    <Card.Body className="ack-body">
                        <p className="ack-plain-message">
                            This alert has <strong>not</strong> been acknowledged yet. Try again, or contact support
                            if the issue persists.
                        </p>
                        <Button
                            variant="primary"
                            className="w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                            onClick={handleAcknowledgeSensorAlert}
                            disabled={acknowledgeSensorAlertLoading}
                        >
                            <i className="bi bi-arrow-repeat" />
                            Try Again
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (viewState === 'already-acknowledged') {
        return (
            <Container fluid className="ack-page-wrapper">
                <Card className="ack-card" role="status">
                    <Card.Body className="ack-header ack-header--info">
                        <div className="ack-icon-ring">
                            <i className="bi bi-info-circle" />
                        </div>
                        <Card.Title as="h1" className="ack-title">Already Acknowledged</Card.Title>
                        <Card.Text className="ack-subtitle">
                            No further action is needed — this alert was already acknowledged.
                        </Card.Text>
                    </Card.Body>
                    <Card.Body className="ack-body">
                        <CloseWindowFooter />
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    const result = ackRes!.result!;
    return (
        <Container fluid className="ack-page-wrapper">
            <Card className="ack-card" role="status">
                <Card.Body className="ack-header ack-header--success">
                    <div className="ack-icon-ring">
                        <i className="bi bi-check-lg" />
                    </div>
                    <Card.Title as="h1" className="ack-title">Alert Acknowledged</Card.Title>
                    <Card.Text className="ack-subtitle">
                        Thanks — this alert has been successfully acknowledged.
                    </Card.Text>
                </Card.Body>
                <Card.Body className="ack-body">
                    <AckSummary result={result} />
                    <CloseWindowFooter />
                </Card.Body>
            </Card>
        </Container>
    );
};

const AckSummary: React.FC<{ result: AckResult }> = ({ result }) => {
    const battery = isBatteryAlert(result.alertType);
    const label = getAlertLabel(result.alertType, result.deviation);
    const isBelow = result.deviation < 0;

    return (
        <div className='ack-summary'>
            <div className="reading-card">
                <Badge bg="" className="alert-badge">
                    <i className={`bi ${battery ? 'bi-battery-half' : 'bi-thermometer-half'}`} />
                    {label}
                </Badge>

                {!battery && (
                    <div className="reading-card-value">
                        <span className="reading-number">{result.reading}</span>
                        <span className="reading-unit">°C</span>
                        <Badge bg="" className={`deviation-badge ${isBelow ? 'deviation-badge--low' : 'deviation-badge--high'}`}>
                            <i className={`bi ${isBelow ? 'bi-arrow-down' : 'bi-arrow-up'}`} />
                            {Math.abs(result.deviation)}°C {isBelow ? 'below' : 'above'} target
                        </Badge>
                    </div>
                )}
            </div>

            <div className="ack-details-grid">
                <div className="detail-item">
                    <span className="detail-icon-wrap">
                        <i className="bi bi-fan" />
                    </span>
                    <div className="detail-text">
                        <span className="detail-value">{result.refrigeratorName}</span>
                        <span className="detail-value-muted">{result.refrigeratorType}</span>
                    </div>
                </div>

                <div className="detail-item">
                    <span className="detail-icon-wrap">
                        <i className="bi bi-clock-history" />
                    </span>
                    <div className="detail-text">
                        <span className="detail-label">Acknowledged by</span>
                        <span className="detail-value detail-value--email">{result.acknowledgedBy}</span>
                        <span className="detail-value-muted">
                            <time dateTime={result.acknowledgedAt}>{formatDateTime(result.acknowledgedAt)}</time>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CloseWindowFooter: React.FC = () => (
    <div className="close-window-footer">
        <p className="close-window-text">You're all set — you can close this window now.</p>
    </div>
);

export default SensorAlertAcknowledgement;